<template>
  <div id="wd-tacking" class="justify-center mx-auto p-4">
    <loading :status="loading"></loading>
    <template v-if="repair.id">
      <div class="w-full flex justify-between">
        <button @click="repair.id = null" class="btn btn-white">
          {{ $t('Back') }}
        </button>
        <button @click="printIt" class="btn btn-white">
          {{ $t('Print with logs') }}
        </button>
        <button @click="printInvoice" class="btn btn-white">
          {{ $t('Print or download invoice') }}
        </button>
      </div>
      <div id="trackContainer">
        <div class="bg-blue-100 rounded-md my-2 p-3 border md:flex justify-between">
          <h4 class="text-1 font-bold tracking-wider">{{ $t('Invoice') }} #{{ repair.tracking }}</h4>
          <h4 class="text-1 font-bold tracking-wider">{{ $t('Total Amount') }} : {{ repair.total_charges | currency }}</h4>
          <h4 class="text-1 font-bold tracking-wider">{{ $t('Additional Amount') }} : {{ repair.additional_amount | currency }}</h4>
          <h4 class="text-1 font-bold tracking-wider text-red-500">{{ $t('Due Amount') }} : {{ repair.due | currency }}</h4>
        </div>
        <div :class="repair.due > 0 ? 'flex place-content-center' : 'hidden'">
          <payment_gateways
            class="self-center w-1/2"
            :isDueProcess="true"
            @start="loading = true"
            @stop="loading = false"
            @process="updateRepairOrder($event)"
          ></payment_gateways>
        </div>
        <div class="mb-1 flex items-center">
          <label class="w-32 block font-bold text-xs uppercase tracking-wide">{{ $t('Status') }}</label>
          <span class="mr-4 inline-block">:</span>
          <div class="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-blue-100 text-blue-800">{{ repair.status }} ({{ repair.updated_at }})</div>
        </div>
        <div class="mb-1 flex items-center">
          <label class="w-32 block font-bold text-xs uppercase">
            {{ $t('Invoice date') }}
          </label>
          <span class="mr-4 inline-block">:</span>
          <div>{{ repair.created_at }}</div>
        </div>
        <div class="mb-1 flex items-center">
          <label class="w-32 block font-bold text-xs uppercase">
            {{ $t('Device collected') }}
          </label>
          <span class="mr-4 inline-block">:</span>
          <div v-if="repair.is_device_collected">{{ $t('Collected') }}</div>
          <div v-else>
            {{ $t('No') }} (<router-link class="text-xs text-blue-700 hover:text-blue-800" :to="`/print/repair-dispatch-info/${repair.uuid}`">
              {{ $t('lets Print dispatch card') }} </router-link
            >)
          </div>
        </div>
        <div class="mb-1 mt-4 flex items-center">
          <label class="w-32 block font-bold text-xs uppercase">
            {{ $t('Device Brand') }}
          </label>
          <span class="mr-4 inline-block">:</span>
          <div>{{ repair.brand }}</div>
        </div>
        <div class="mb-1 flex items-center">
          <label class="w-32 block font-bold text-xs uppercase">
            {{ $t('Device') }}
          </label>
          <span class="mr-4 inline-block">:</span>
          <div>{{ repair.device.name }}</div>
        </div>
        <div class="mb-1 flex items-center">
          <label class="w-32 block font-bold text-xs uppercase tracking-wide">
            {{ $t('Device Model') }}
          </label>
          <span class="mr-4 inline-block">:</span>
          <div>{{ repair.device.model }}</div>
        </div>
        <div class="mb-1 flex items-center">
          <label class="w-32 block font-bold text-xs uppercase tracking-wide">{{ $t('Serial number') }}</label>
          <span class="mr-4 inline-block">:</span>
          <div>{{ repair.serial_number }}</div>
        </div>

        <template v-if="logs.length">
          <div class="block mt-4" id="wd-tacking-logs">
            <div class="text-xs text-gray-800 text-center bg-gray-200">
              {{ $t('Repair activity') }}
            </div>
            <template v-for="(log, index) in logs">
              <div :class="{ 'border-t': index > 0 }" :key="index" class="flex p-6 bg-white border-l-2">
                <template v-if="log.user">
                  <img :alt="$t('Avatar')" :src="log.user.avatar !== 'gravatar' ? log.user.avatar : log.user.gravatar" class="h-12 w-12 rounded-md hidden sm:inline" />
                </template>
                <div class="sm:pl-6 pb-2 w-full">
                  <div class="md:flex md:items-center pb-1">
                    <div class="md:flex-1 text-sm font-semibold text-gray-800" v-if="log.user">
                      {{ log.user.name | capitalize }}
                      <span class="text-xs text-gray-500">({{ log.user.role_title }})</span>
                    </div>
                    <div class="md:flex-1">
                      <div class="md:float-right text-sm">
                        {{ log.created_at }}
                      </div>
                    </div>
                  </div>
                  <p class="text-gray-900 text-xs" v-html="log.body"></p>
                </div>
              </div>
            </template>
          </div>
        </template>
      </div>
    </template>
    <template v-else>
      <div class="relative flex flex-col min-w-0 break-words w-full shadow-lg rounded-lg bg-yellow-300">
        <form @submit.prevent="track">
          <div class="flex-auto p-5 lg:p-15">
            <h4 class="text-3xl font-semibold">
              {{ $t('Want to track repair order ?') }}
            </h4>
            <p class="leading-relaxed mt-1 mb-4 text-gray-600">
              {{ $t('Please enter your invoice tracking identity number to trace your repair order status.') }}
            </p>
            <div class="relative w-full mb-3 mt-8">
              <label class="block uppercase text-gray-700 text-xs font-bold mb-2" for="track_id">{{ $t('Enter track ID') }}</label>
              <input :placeholder="$t('Enter track ID')" class="form-input" id="track_id" required type="text" v-model="track_id" />
              <span v-if="errorMessage" class="px-1 text-red-500">{{ errorMessage }}.</span>
            </div>

            <div class="text-center mt-6">
              <button class="btn btn-secondary px-5 uppercase" type="submit">
                {{ $t('Track your order status') }}
              </button>
            </div>
          </div>
        </form>
      </div>
    </template>
  </div>
</template>
<script>
import Gateways from '@/components/payments/gateways';
export default {
  name: 'repair-track',
  metaInfo() {
    return {
      title: this.$i18n.t('Track repair order status'),
      titleTemplate: `%s - ${this.$store.state.settings.name}`,
    };
  },
  components: {
    payment_gateways: Gateways,
  },
  data() {
    return {
      loading: false,
      track_id: null,
      logs: [],
      errorMessage: null,
      repair: {
        id: null,
        due: 1,
      },
    };
  },
  mounted() {
    const pathArray = window.parent.location.pathname.split('/');
    if (pathArray[1] === 'track' && pathArray[2]) {
      this.track_id = pathArray[2];
      this.track();
    }
    if (this.$route.params.key) {
      this.track_id = this.$route.params.key;
      this.track();
    }
  },
  methods: {
    async printIt() {
      await this.$htmlToPaper('trackContainer');
    },
    printInvoice() {
      this.$router.push(`/print/repair-order/${this.repair.uuid}`);
    },
    updateRepairOrder(gateway) {
      this.loading = true;
      this.repair.payment_gateway_key = gateway.key;
      this.repair.payment_gateway_value = gateway.value;
      axios.post(route('repair.pay-due', this.repair.uuid), this.repair).then((response) => {
        this.$notify({
          title: this.$i18n.t('Warning').toString(),
          text: response.data.message.toString(),
          type: 'success',
        });
        if (response.data.repair) {
          this.repair = response.data.repair;
          this.logs = response.data.logs;
        }
        this.loading = false;
      });
    },
    async track() {
      this.loading = true;
      if (this.$store.state.settings.recaptcha_enabled) {
        await this.$recaptchaLoaded();
        const token = await this.$recaptcha('login');
        this.submitTotrack({ track_id: this.track_id, captcha_hash: token });
      } else {
        this.submitTotrack({ track_id: this.track_id });
      }
    },
    submitTotrack(track) {
      axios
        .post(route('repair.track'), track)
        .then((response) => {
          if (response.data.repair) {
            this.repair = response.data.repair;
            this.logs = response.data.logs;
          } else {
            this.errorMessage = response.data.message;
            this.$notify({
              title: this.$i18n.t('Warning').toString(),
              text: response.data.message.toString(),
              type: 'warning',
            });
          }
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
  },
};
</script>
